import { useTableScrollHints } from "../useTableScrollHints";
import { useRef, useState } from "react";
import { renderMarkdown } from "../md";
import type { AgentsSnapshot, IdeaChannel, SubAgentRun, TodoItem, WorkflowRun } from "../protocol";

/** Right rail for the chat view: the session's live to-do checklist. When there are no to-dos (e.g. the
 *  agent's to-do tool is unavailable) but an idea is being built, it falls back to showing that idea.
 *  `inline` renders it as the To-dos tab's full-width pane instead, for screens too narrow for the rail. */
export function TodosRail({
  todos,
  inProgressIdea,
  agents,
  inline = false,
}: {
  todos: TodoItem[];
  inProgressIdea?: IdeaChannel;
  agents?: AgentsSnapshot;
  inline?: boolean;
}) {
  return (
    <aside className={inline ? "todos-pane" : "todos-rail"}>
      <div className="rail-head">
        To-dos{todos.length ? ` · ${todos.filter((t) => t.status !== "completed").length} active` : ""}
      </div>
      <div className="rail-body">
        {todos.length === 0 ? (
          inProgressIdea ? (
            <div className="todo in_progress">
              <span className="check">◐</span>
              <span className="todo-text">Building: {inProgressIdea.title || "(untitled idea)"}</span>
            </div>
          ) : (
            <p className="muted" style={{ padding: 16 }}>
              No to-dos right now.
            </p>
          )
        ) : (
          todos.map((t, i) => (
            <div key={i} className={`todo ${t.status}`}>
              <span className="check">
                {t.status === "completed" ? "✓" : t.status === "in_progress" ? "◐" : "○"}
              </span>
              <span className="todo-text">{t.content}</span>
            </div>
          ))
        )}
        {agents && (agents.workflows.length > 0 || agents.subagents.length > 0) && <AgentsSection agents={agents} />}
      </div>
    </aside>
  );
}

/** A span in its coarsest useful unit: "40s", "12m", "3h 05m". */
export function spanLabel(ms: number): string {
  const secs = Math.max(0, Math.floor(ms / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}

/** Each of a run's phases with how far the run has got through it. */
export function phaseStates(run: WorkflowRun): Array<{ name: string; state: "done" | "running" | "failed" | "pending" }> {
  const at = run.phases.indexOf(run.phase);
  return run.phases.map((name, i) => ({
    name,
    state:
      run.state === "completed" || i < at ? "done" : i === at ? (run.state === "running" ? "running" : "failed") : "pending",
  }));
}

function runStatus(run: WorkflowRun): string {
  if (run.state === "running") return spanLabel(Date.now() - run.startedAt);
  return run.state === "completed" ? "Finished" : run.state === "killed" ? "Stopped" : "Failed";
}

/** The session's workflow runs and sub-agents, below its to-dos. A run expands to every agent it launched. */
function AgentsSection({ agents }: { agents: AgentsSnapshot }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="agents">
      {agents.workflows.length > 0 && <div className="agents-head">Workflows</div>}
      {agents.workflows.map((w) => (
        <WorkflowCard key={w.runId} run={w} open={open === w.runId} onToggle={() => setOpen(open === w.runId ? null : w.runId)} />
      ))}
      {agents.subagents.length > 0 && <div className="agents-head">Sub-agents</div>}
      {agents.subagents.map((s) => (
        <SubAgentRow key={s.id} sub={s} />
      ))}
    </div>
  );
}

function WorkflowCard({ run, open, onToggle }: { run: WorkflowRun; open: boolean; onToggle: () => void }) {
  const working = run.agents.filter((a) => a.state === "running");
  const lastDone = [...run.agents].reverse().find((a) => a.state === "done" && a.result);
  const last = run.log ?? (lastDone ? `${lastDone.label}: ${lastDone.result}` : "");
  const stats = [
    `${run.agentCount} agents`,
    run.tokens != null ? `${run.tokens.toLocaleString()} tokens` : null,
    run.toolCalls != null ? `${run.toolCalls.toLocaleString()} tool calls` : null,
  ].filter(Boolean);
  return (
    <div className="wf-card">
      <button className="wf-top" onClick={onToggle} aria-expanded={open}>
        <span className="wf-title">{run.title}</span>
        {run.name !== run.title && <span className="wf-name">{run.name}</span>}
        <span className="wf-status">{runStatus(run)}</span>
      </button>
      {run.phases.length > 0 && (
        <div className="wf-phases">
          {phaseStates(run).map((p) => (
            <div key={p.name} className={`wf-phase ${p.state}`}>
              <span className="wf-bar" />
              <span className="wf-phase-name">{p.name}</span>
            </div>
          ))}
        </div>
      )}
      {working.slice(-3).map((a, i) => (
        <div key={i} className="wf-working">
          <span className="dot working" />
          <span className="wf-agent">{a.label}</span>
          {a.doing && <span className="wf-doing">{a.doing}</span>}
        </div>
      ))}
      {last && <div className="wf-last">{last}</div>}
      {open && (
        <div className="wf-detail">
          <div className="wf-stats">{stats.join(" · ")}</div>
          {run.summary && <div className="wf-summary">{run.summary}</div>}
          {[...run.agents]
            .reverse()
            .filter((a) => a.state === "done")
            .map((a, i) => (
              <div key={i} className="wf-step">
                <div>
                  <span className="wf-agent">{a.label}</span> <span className="wf-step-phase">{a.phase}</span>
                </div>
                {a.result && <div className="wf-result">{a.result}</div>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function SubAgentRow({ sub }: { sub: SubAgentRun }) {
  const where = [sub.worktree ? "worktree" : null, sub.background ? "background" : "foreground", sub.state === "done" ? "done" : null]
    .filter(Boolean)
    .join(" · ");
  const line = sub.state === "running" ? sub.doing : sub.result;
  return (
    <div className="sub-row">
      <div>
        <span className={`dot ${sub.state === "running" ? "working" : "idle-dot"}`} />
        <span className="wf-agent">{sub.title || "Sub-agent"}</span>
      </div>
      <div className="wf-name">{where}</div>
      {line && <div className={sub.state === "running" ? "wf-doing" : "wf-result"}>{line}</div>}
    </div>
  );
}

/** Right rail for an open idea: its living spec document, rendered as markdown. */
export function SpecRail({ doc }: { doc: string }) {
  const mdRef = useRef<HTMLDivElement>(null);
  useTableScrollHints(mdRef, [doc]);
  return (
    <aside className="todos-rail spec-rail">
      <div className="rail-head">Spec</div>
      <div className="rail-body">
        {doc ? (
          <div ref={mdRef} className="bubble-text md spec-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(doc) }} />
        ) : (
          <p className="muted" style={{ padding: 16 }}>
            No spec yet.
          </p>
        )}
      </div>
    </aside>
  );
}
